// Outer
import * as Sentry from '@sentry/nextjs';
import React, { useMemo } from 'react';
import * as yup from 'yup';
import { FormikProvider, useFormik } from 'formik';
import { Box, Button, FormGroup, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation } from 'react-query';

// Inner
import FormikTextField from '@/common/components/forms/TextField';
import ReactQuillField from '@/common/components/forms/ReactQuillField';
import { useApiContext } from '@/common/contexts/ApiContext';
import {
  ApiError,
  FAQUpdateVars,
  FaqSubmitValue,
} from '@/common/api/types';

// Methods
function getInitialValues(faq: any) {
  return {
    question: faq?.question ?? '',
    answer: faq?.answer ?? '',
  }
}
const validationSchema = yup.object({
  question: yup.string().required(),
  answer: yup.string().required(),
});

function useUpdateFAQForm(faqId: number, refetch: () => void, onClose: () => void, faq: any) {
  const { enqueueSnackbar } = useSnackbar();
  const { apiClient } = useApiContext();

  const updateFAQMutation = useMutation<
    any,
    ApiError | Error,
    FAQUpdateVars
  >(apiClient.faq.update, {
    onSuccess: onSiteFAQSuccess,
    onError,
  });

  const initialValues = useMemo(
    () => getInitialValues(faq[0]),
    [faq],
  );

  const formik = useFormik<any>({
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  function onSiteFAQSuccess() {
    enqueueSnackbar('FAQ updated', { variant: 'success' });
    refetch()

    formik.setSubmitting(false);
    onClose();
  }
  function onError(error: ApiError | Error) {
    enqueueSnackbar(error.message, { variant: 'error' });
    Sentry.captureException(error);
    formik.setSubmitting(false);
  }

  function onSubmit(values: FaqSubmitValue) {

    updateFAQMutation.mutate({
      ...values,
      faqId,
    });
  }

  return {
    formik,
  };
}

export default function UpdateFAQForm({
  faqId,
  refetch,
  onClose,
  faqs
}: any) {
  const faq = useMemo(
    () => faqs.filter((FAQ: any) => FAQ.id === faqId),
    [faqs, faqId],
  );
  const { formik } = useUpdateFAQForm(faqId, onClose, refetch, faq);

  return (
    <FormikProvider value={formik}>
      <Box margin={2} display="flex" flexDirection="column">
        <FormGroup>
          <Box marginTop={2}>
            <Typography>Edit Question</Typography>
          </Box>

          <Box marginTop={2}>
            <FormikTextField
              fullWidth
              id="question"
              name="question"
              type="text"
              label="Question"
              tooltiptitle="Enter the question."
              variant="outlined"
            />
          </Box>

          <Box marginTop={2}>
            <ReactQuillField
              id="answer"
              name="answer"
              label="Answer"
              tooltiptitle="Enter the answer to the question."
            />
          </Box>

        </FormGroup>

        <Box marginTop={1} marginBottom={1}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disabled={false} // TODO: Add Logic
            onClick={formik.submitForm}
          >
            Save
          </Button>
        </Box>
        <Button
          variant="outlined"
          color="primary"
          onClick={onClose}
        >
          Cancel
        </Button>
      </Box>
    </FormikProvider>
  );
}
